/**
 * Generic category filter.
 *
 * Looks for containers with [data-filter], which hold buttons with
 * [data-filter-value].  Filterable items are any element on the page
 * that carries a [data-categories] attribute (space-separated list of
 * slugs).
 *
 * Optional [data-filter-url-param] (e.g. "category") syncs the active
 * filter to the query string for shareable URLs on news / resources.
 *
 * Optional path aliases:
 *   data-filter-home="/news"
 *   data-filter-paths='{"newsletters":"/newsletters"}'
 *   data-filter-initial="newsletters"
 *   data-filter-extra-values="newsletters"
 */

const groupApplyHandlers = new WeakMap();
let popstateBound = false;

function normalizePath(pathname) {
  if (!pathname) return "/";
  const trimmed = pathname.replace(/\/+$/, "");
  return trimmed === "" ? "/" : trimmed;
}

function parseFilterPaths(raw) {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    return Object.fromEntries(
      Object.entries(parsed).filter(
        ([slug, path]) => slug && typeof path === "string"
      )
    );
  } catch {
    return {};
  }
}

function readFilterFromUrl(paramName) {
  if (!paramName) return null;
  const value = new URLSearchParams(window.location.search).get(paramName);
  return value && value.trim() !== "" ? value.trim() : null;
}

function readFilterFromLocation(group) {
  const paths = parseFilterPaths(group.dataset.filterPaths);
  const currentPath = normalizePath(window.location.pathname);

  for (const [slug, path] of Object.entries(paths)) {
    if (normalizePath(path) === currentPath) {
      return slug;
    }
  }

  const fromQuery = readFilterFromUrl(group.dataset.filterUrlParam);
  if (fromQuery) return fromQuery;

  if (group.dataset.filterInitial) {
    return group.dataset.filterInitial;
  }

  return null;
}

function writeFilterToUrl(group, value) {
  const paramName = group.dataset.filterUrlParam || "";
  const paths = parseFilterPaths(group.dataset.filterPaths);
  const home = group.dataset.filterHome || window.location.pathname;
  const url = new URL(window.location.href);

  if (value && paths[value]) {
    url.pathname = paths[value];
    if (paramName) url.searchParams.delete(paramName);
  } else {
    url.pathname = home;
    if (paramName) {
      if (!value || value === "all") {
        url.searchParams.delete(paramName);
      } else {
        url.searchParams.set(paramName, value);
      }
    }
  }

  const next = url.pathname + url.search + url.hash;
  const current =
    window.location.pathname + window.location.search + window.location.hash;
  if (next !== current) {
    history.replaceState(null, "", next);
  }
}

function bindPopstate() {
  if (popstateBound) return;
  popstateBound = true;
  window.addEventListener("popstate", () => {
    document.querySelectorAll("[data-filter]").forEach((group) => {
      const applyFilter = groupApplyHandlers.get(group);
      if (!applyFilter) return;
      applyFilter(readFilterFromLocation(group), {
        updateUrl: false,
      });
    });
  });
}

export function initFilters() {
  const filterGroups = document.querySelectorAll("[data-filter]");
  if (filterGroups.length === 0) return;

  const items = Array.from(document.querySelectorAll("[data-categories]"));
  if (items.length === 0) return;

  bindPopstate();

  filterGroups.forEach((group) => {
    const buttons = Array.from(
      group.querySelectorAll("[data-filter-value]")
    );
    if (buttons.length === 0) return;

    const extraValues = (group.dataset.filterExtraValues || "")
      .split(/\s+/)
      .filter(Boolean);
    const validValues = new Set(
      buttons
        .map((btn) => btn.dataset.filterValue)
        .filter(Boolean)
        .concat(extraValues)
    );

    const resolveFilterValue = (requested) => {
      if (requested && validValues.has(requested)) {
        return requested;
      }
      return "all";
    };

    const applyFilter = (value, { updateUrl = true } = {}) => {
      const active = resolveFilterValue(value);

      buttons.forEach((btn) => {
        const isActive = btn.dataset.filterValue === active;
        btn.classList.toggle("is-active", isActive);
        btn.setAttribute("aria-pressed", String(isActive));
      });

      items.forEach((item) => {
        const categories = (item.dataset.categories || "")
          .split(" ")
          .filter(Boolean);
        const show = active === "all" || categories.includes(active);
        item.classList.toggle("is-hidden", !show);
      });

      if (updateUrl) {
        writeFilterToUrl(group, active);
      }
    };

    groupApplyHandlers.set(group, applyFilter);

    if (!group.dataset.filterClickBound) {
      group.dataset.filterClickBound = "true";
      group.addEventListener("click", (event) => {
        const btn = event.target.closest("[data-filter-value]");
        if (!btn || !group.contains(btn)) return;
        applyFilter(btn.dataset.filterValue);
      });
    }

    const requested = readFilterFromLocation(group);
    const initial = resolveFilterValue(requested);
    const paths = parseFilterPaths(group.dataset.filterPaths);
    const shouldCanonicalize =
      Boolean(paths[initial]) &&
      normalizePath(paths[initial]) !== normalizePath(window.location.pathname);

    applyFilter(initial, { updateUrl: shouldCanonicalize });
  });
}
